import { SetStateAction } from "react";

import { SearchInput } from "@bridgesplit/ui";
import { LendingVaultExpanded } from "@bridgesplit/abf-react";
import { ExternalYieldSource, StrategyDuration } from "@bridgesplit/abf-sdk";

import { useManagerViewContext } from "./ManagerViewContext";
import { CollateralForm, initialSettingsForm, RateForm, SettingForm } from "./type";
import { convertVaultExpandedToMintToInfo, convertVaultExpandedToMintToRateInfo } from "./util";

type FormType = "collateralForm" | "rateForm";

type FormTypes = {
    collateralForm: CollateralForm;
    rateForm: RateForm;
};

function useFormSearch<T extends FormType>(formType: T) {
    const { collateralForm, setCollateralForm, rateForm, setRateForm } = useManagerViewContext();

    type CurrentForm = FormTypes[T];

    const form = formType === "collateralForm" ? collateralForm : rateForm;
    const setForm = formType === "collateralForm" ? setCollateralForm : setRateForm;

    return {
        form,
        setForm: setForm as React.Dispatch<React.SetStateAction<CurrentForm>>
    };
}

export function SearchCollateral({ formType = "collateralForm" }: { formType?: FormType }) {
    const { form, setForm } = useFormSearch(formType);

    return (
        <SearchInput
            fullWidth
            placeholder="Search by token or address"
            onChange={(e) =>
                setForm((prev) => ({
                    ...prev,
                    search: e.target.value
                }))
            }
            value={form.search}
        />
    );
}

type SetFormFunction<T> = (value: SetStateAction<T>) => void;

export const FORM_RESET_HANDLERS = {
    collateralForm: (vaultExpanded: LendingVaultExpanded | undefined) => (prev: CollateralForm) => ({
        ...prev,
        ...updateCollateralForm(vaultExpanded)
    }),

    rateForm:
        (vaultExpanded: LendingVaultExpanded | undefined, strategyDurations: StrategyDuration[] | undefined) =>
        (prev: RateForm) => ({
            ...prev,
            ...updateRateForm(vaultExpanded, strategyDurations)
        }),
    settingsForm: (vaultExpanded: LendingVaultExpanded | undefined) => () => ({
        ...initialSettingsForm,
        idleCapital: vaultExpanded?.vaultStrategy.externalYieldInfo?.externalYieldSource ?? ExternalYieldSource.None,
        originationsEnabled: vaultExpanded?.vaultStrategy.strategy.originationsEnabled
    })
};

const updateCollateralForm = (vaultExpanded: LendingVaultExpanded | undefined) => ({
    mintToInfo: convertVaultExpandedToMintToInfo(vaultExpanded)
});

const updateRateForm = (
    vaultExpanded: LendingVaultExpanded | undefined,
    strategyDurations: StrategyDuration[] | undefined
) => {
    const rateInfo = convertVaultExpandedToMintToRateInfo(vaultExpanded, strategyDurations);
    if (!rateInfo) return { mintToRateInfo: {}, disabledMints: {} };
    const disabledMints: Record<string, string[]> = {};

    // Process disabled mints based on missing APY values from rateInfo

    for (const [mint, durations] of Object.entries(rateInfo)) {
        const disabledDurations: string[] = [];

        for (const [duration, info] of Object.entries(durations)) {
            if (!info.apyAmountUi) {
                disabledDurations.push(duration);
            }
        }

        if (disabledDurations.length > 0) {
            disabledMints[mint] = disabledDurations;
        }
    }

    return {
        mintToRateInfo: Object.fromEntries(Object.entries(rateInfo).map(([mint, info]) => [mint, info])),
        disabledMints
    };
};

export const resetAllForms = (
    vaultExpanded: LendingVaultExpanded | undefined,
    strategyDurations: StrategyDuration[] | undefined,
    setCollateralForm: SetFormFunction<CollateralForm>,
    setRateForm: SetFormFunction<RateForm>,
    setSettingsForm: SetFormFunction<SettingForm>
) => {
    setCollateralForm((prev) => ({ ...prev, ...updateCollateralForm(vaultExpanded) }));
    setRateForm((prev) => ({ ...prev, ...updateRateForm(vaultExpanded, strategyDurations) }));
    setSettingsForm((prev) => ({ ...prev, ...FORM_RESET_HANDLERS.settingsForm(vaultExpanded)() }));
};
